import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type FlagMap = Record<string, { enabled: boolean; payload: unknown }>;

let cache: FlagMap | null = null;
const listeners = new Set<() => void>();
const CACHE_KEY = "ss2026_flags";

async function fetchFlags() {
  try {
    const { data } = await supabase.from("feature_flags").select("key,enabled,payload");
    const map: FlagMap = {};
    (data ?? []).forEach((r) => {
      map[r.key] = { enabled: r.enabled, payload: r.payload };
    });
    cache = map;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch {
      /* noop */
    }
    listeners.forEach((l) => l());
  } catch {
    /* noop */
  }
}

if (typeof window !== "undefined") {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) cache = JSON.parse(raw) as FlagMap;
  } catch {
    /* noop */
  }
  void fetchFlags();
}

export function useFeatureFlag(key: string, defaultValue = false): boolean {
  const [val, setVal] = useState<boolean>(() => cache?.[key]?.enabled ?? defaultValue);
  useEffect(() => {
    const cb = () => setVal(cache?.[key]?.enabled ?? defaultValue);
    listeners.add(cb);
    cb();
    return () => {
      listeners.delete(cb);
    };
  }, [key, defaultValue]);
  return val;
}
